import {
  ActuacionesAU,
  IndicadoresAU,
  ProjectAU,
  ValoresAU,
} from "@/types/types";

const stripBOM = (s: string) => s.replace(/^\uFEFF/, "");
const norm = (s: string) => stripBOM(s).toLowerCase().trim();
const normCode = (s: string) => stripBOM(s).trim().toUpperCase();

const htmlEscape = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const looksLikeBullet = (line: string) => /^(\s*[-*•]\s+).+/.test(line);
const looksLikeNumbered = (line: string) => /^(\s*\d+[\.)]?\s+).+/.test(line);

const plainTextToHtml = (input?: string): string | undefined => {
  const raw = stripBOM(input ?? "").trim();
  if (!raw) return undefined;

  const blocks = raw
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split(/\n{2,}/);

  const out: string[] = [];

  for (const block of blocks) {
    const lines = block
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    if (!lines.length) continue;

    const isUl = lines.every((l) => looksLikeBullet(l));
    const isOl = !isUl && lines.every((l) => looksLikeNumbered(l));

    if (isUl || isOl) {
      const tag = isUl ? "ul" : "ol";
      const items = lines
        .map((l) =>
          l
            .replace(isUl ? /^[-*•]\s+/ : /^\d+[\.)]?\s+/, "")
            .trim()
        )
        .map((text) => `<li>${htmlEscape(text)}</li>`)
        .join("");
      out.push(`<${tag}>${items}</${tag}>`);
    } else {
      const html = lines.map((l) => htmlEscape(l)).join("<br>");
      out.push(`<p>${html}</p>`);
    }
  }

  return out.join("");
};

const toIsoDate = (s?: string): string | undefined => {
  if (!s) return undefined;
  const raw = s.trim();
  if (!raw) return undefined;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

  const parts = raw.split(/[\/\-]/).map((p) => p.trim());
  if (parts.length !== 3) return undefined;

  let d: number;
  let m: number;
  let y: number;

  if (/^\d{4}$/.test(parts[0])) {
    y = Number(parts[0]);
    m = Number(parts[1]);
    d = Number(parts[2]);
  } else {
    d = Number(parts[0]);
    m = Number(parts[1]);
    y = Number(parts[2]);
  }

  if (y < 100) y = y < 50 ? 2000 + y : 1900 + y;

  const dd = String(d).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const yyyy = String(y).padStart(4, "0");

  if (m < 1 || m > 12 || d < 1 || d > 31) return undefined;

  return `${yyyy}-${mm}-${dd}`;
};

const parseFechasFromRow = (
  r: Record<string, string>
): { fecha_inicio?: string; fecha_fin?: string } => {
  let inicioRaw =
    r["fecha inicio"] ?? r["fecha_inicio"] ?? r["inicio"] ?? undefined;
  let finRaw = r["fecha fin"] ?? r["fecha_fin"] ?? r["fin"] ?? undefined;

  if (!inicioRaw && !finRaw) {
    const range = r["fechas"] ?? r["fecha"];
    if (range) {
      const [a, b] = range.split(",").map((s) => s?.trim());
      inicioRaw = a;
      finRaw = b;
    }
  }

  return {
    fecha_inicio: toIsoDate(inicioRaw),
    fecha_fin: toIsoDate(finRaw),
  };
};

const parseCsv = (csv: string): { headers: string[]; rows: string[][] } => {
  const rows: string[][] = [];
  let curField = "";
  let curRow: string[] = [];
  let inQuotes = false;

  const pushField = () => {
    curRow.push(stripBOM(curField));
    curField = "";
  };
  const pushRow = () => {
    if (curRow.length && curRow.some((c) => c.trim() !== "")) rows.push(curRow);
    curRow = [];
  };

  for (let i = 0; i < csv.length; i++) {
    const ch = csv[i];
    if (ch === '"') {
      if (inQuotes && csv[i + 1] === '"') {
        curField += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      pushField();
    } else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && csv[i + 1] === "\n") i++;
      pushField();
      pushRow();
    } else {
      curField += ch;
    }
  }

  pushField();
  pushRow();

  if (!rows.length) return { headers: [], rows: [] };

  const headers = rows[0].map((h) => norm(h));
  const data = rows.slice(1);
  return { headers, rows: data };
};

const csvToObjects = (csv: string): Record<string, string>[] => {
  const { headers, rows } = parseCsv(csv);
  if (!headers.length) return [];
  return rows.map((cols) => {
    const rec: Record<string, string> = {};
    for (let i = 0; i < headers.length; i++) {
      rec[headers[i]] = stripBOM((cols[i] ?? "").trim());
    }
    return rec;
  });
};

const parseCsvNumber = (raw?: string): number | undefined => {
  if (!raw) return undefined;
  const n = Number(raw.replace(/[%\s]/g, "").replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : undefined;
};

const splitList = (raw?: string): string[] | undefined => {
  if (!raw) return undefined;
  const parts = raw
    .split(",")
    .map((s) =>
      s
        .trim()
        .replace(/^oe\s*/i, "")
        .replace(/^ods\s*/i, "")
        .trim()
    )
    .filter(Boolean);
  return parts.length ? parts : undefined;
};

export const parseProjectsCsv = async (csv: string): Promise<ProjectAU[]> => {
  const rows = csvToObjects(csv);

  return rows.map((r) => {
    const nombre = stripBOM(r["nombre"] ?? "").trim();
    const beneficiarios = plainTextToHtml(r["beneficiarios"]);
    const observaciones = plainTextToHtml(r["observaciones"]);
    const descripcion = plainTextToHtml(r["descripcion"]);
    const actores = plainTextToHtml(r["actores"]);
    const potenciales_fuentes_financiacion = plainTextToHtml(
      r["potenciales_fuentes_financiacion"] ??
        r["potenciales fuentes financiacion"] ??
        r["potenciales_fuentes_financiación"] ??
        r["potenciales fuentes financiación"] ??
        r["potenciales_fuentes_financiacion "] ??
        r["potenciales fuentes financiacion "]
    );
    const { fecha_inicio, fecha_fin } = parseFechasFromRow(r);

    return {
      codigo: stripBOM(r["codigo"] ?? "").trim(),
      tipo: r["tipo"],
      nombre,
      beneficiarios,
      observaciones,
      descripcion,
      actores,
      potenciales_fuentes_financiacion,
      fecha_inicio,
      fecha_fin,
      area: r["area"] ?? "",
      estado: r["estado"] ?? "",
      caracter: r["caracter"] || undefined,
      horizonte_temporal:
        r["horizonte temporal"] ?? r["horizonte_temporal"] ?? undefined,
      prioridad: r["prioridad"] ?? "",
      presupuesto_estimado: parseCsvNumber(
        r["presupuesto estimado"] ?? r["presupuesto_estimado"]
      ),
      oe: splitList(r["oe"]),
      ods: splitList(r["ods"]),
      indicadores: undefined,
      actuaciones: undefined,
    };
  });
};

export const parseIndicadoresCsv = async (
  csv: string,
  tenant_id?: number
): Promise<IndicadoresAU[]> => {
  const rows = csvToObjects(csv);
  const currentYear = String(new Date().getFullYear());

  const toBoolean = (value: unknown): boolean => {
    const normalized = stripBOM(String(value ?? "")).trim().toLowerCase();
    return ["true", "1", "sí", "si", "yes", "y"].includes(normalized);
  };

  return rows.map<IndicadoresAU>((r) => {
    const proyectoCodigo = stripBOM(
      r["proyecto_codigo"] ?? r["proyecto"] ?? r["Proyecto"] ?? ""
    ).trim();
    const codigo = stripBOM(r["codigo"] ?? r["Codigo"] ?? "").trim();
    const descripcion = stripBOM(
      r["descripcion"] ?? r["Descripcion"] ?? ""
    ).trim();
    const tipo = stripBOM(r["tipo"] ?? r["Tipo"] ?? "").trim();
    const unidad = stripBOM(r["unidad"] ?? r["Unidad"] ?? "").trim();
    const es_numerico = toBoolean(
      r["es_numerico"] ??
        r["numerico"] ??
        r["numérico"] ??
        r["Numerico"] ??
        r["Numérico"]
    );
    const valor_optimo = parseCsvNumber(
      r["valor_optimo"] ??
        r["valor optimo"] ??
        r["valor óptimo"] ??
        r["Valor Optimo"] ??
        r["Valor Óptimo"]
    );
    const valor = parseCsvNumber(
      r["valor"] ??
        r["valor_actual"] ??
        r["valor actual"] ??
        r["Valor"] ??
        r["Valor Actual"]
    );
    const estado = stripBOM(r["estado"] ?? r["Estado"] ?? "").trim();
    const valores_aue: ValoresAU[] =
      es_numerico && valor !== undefined && valor !== null
        ? [{ valor, ts: currentYear, indicador: 0 } as ValoresAU]
        : [];

    return {
      codigo,
      descripcion: descripcion || undefined,
      tipo,
      unidad: unidad || undefined,
      valor_optimo: valor_optimo ?? undefined,
      proyecto: proyectoCodigo
        ? ({ codigo: proyectoCodigo } as ProjectAU)
        : undefined,
      tenant: tenant_id,
      valores_aue,
      es_numerico,
      estado: es_numerico ? "" : estado,
      translations: [],
    };
  });
};

export const parseActuacionesCsv = async (
  csv: string,
  tenant_id?: number
): Promise<ActuacionesAU[]> => {
  const rows = csvToObjects(csv);

  return rows.map<ActuacionesAU>((r) => {
    const proyectoCodigo = r["proyecto_codigo"] || r["proyecto"];
    const codigo = stripBOM(r["codigo"] ?? "").trim();
    const nombre = stripBOM(r["nombre"] ?? "").trim();
    const presupuesto = parseCsvNumber(r["presupuesto"]) ?? 0;

    return {
      codigo,
      nombre,
      presupuesto,
      proyecto: proyectoCodigo
        ? ({ codigo: stripBOM(proyectoCodigo).trim() } as ProjectAU)
        : undefined,
      tenant_id,
    } as ActuacionesAU;
  });
};

export const attachChildrenToProjects = (args: {
  projects: ProjectAU[];
  indicadores?: IndicadoresAU[];
  actuaciones?: ActuacionesAU[];
}): ProjectAU[] => {
  const { projects, indicadores = [], actuaciones = [] } = args;
  const byCodigo = new Map<string, ProjectAU>();
  for (const p of projects) byCodigo.set(normCode(p.codigo), p);

  for (const ind of indicadores) {
    const codigo =
      typeof ind.proyecto === "object" && ind.proyecto !== null
        ? ind.proyecto.codigo
        : undefined;
    if (!codigo) continue;
    const proj = byCodigo.get(normCode(codigo));
    if (!proj) continue;
    (proj.indicadores ??= []).push({ ...ind, proyecto: undefined });
  }

  for (const act of actuaciones) {
    const codigo =
      typeof act.proyecto === "object" && act.proyecto !== null
        ? act.proyecto.codigo
        : undefined;
    if (!codigo) continue;
    const proj = byCodigo.get(normCode(codigo));
    if (!proj) continue;
    (proj.actuaciones ??= []).push({ ...act, proyecto: undefined });
  }

  return projects;
};

